import { formatRupees } from '@quickreport/domain';
import { Badge } from '@quickreport/ui';
import {
  deleteReport,
  type ReportDeletionImpact,
  reportDeletionImpact,
} from '@quickreport/workspace';
import { useEffect, useState } from 'react';
import { Consequence, DeleteConfirm } from '../../components/DeleteConfirm';
import { useWorkspace } from '../../lib/workspace';
import { STATUS_LABEL, STATUS_TONE, useCtx } from './context';

const money = (paise: number) => formatRupees(paise);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Ask before deleting one report, saying what goes with it. */
export function DeleteReportDialog({
  reportId,
  onBeforeDelete,
  onDeleted,
  onClose,
}: {
  reportId: string;
  /** Called just before deleting, so the page can stop saving the report. */
  onBeforeDelete?: () => void;
  onDeleted: () => void;
  onClose: () => void;
}) {
  const ws = useWorkspace();
  const ctx = useCtx();
  const [impact, setImpact] = useState<ReportDeletionImpact | null>(null);

  useEffect(() => {
    void reportDeletionImpact(ws.db, ctx.roles, reportId).then(setImpact);
  }, [ws.db, ctx.roles, reportId]);

  if (!impact) return null;

  return (
    <DeleteConfirm
      title="Delete this report?"
      subject={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{impact.name}</span>
            <Badge tone={STATUS_TONE[impact.status] ?? 'neutral'}>
              {STATUS_LABEL[impact.status] ?? impact.status}
            </Badge>
          </div>
          <div className="mt-0.5 text-slate">
            <span className="font-mono">{impact.labNo}</span>
            {impact.patient ? ` · ${impact.patient}` : ''}
          </div>
        </>
      }
      consequences={
        <>
          <Consequence>
            The report disappears from your reports, search, reminders and dashboard.
          </Consequence>
          {impact.signed ? (
            <Consequence tone="warn">
              <b>It is already signed.</b>{' '}
              {impact.hasLink
                ? 'The QR code and the link on any copy the patient has will stop working.'
                : 'Any copy already given to the patient is no longer backed by your records.'}
            </Consequence>
          ) : null}
          {impact.payments > 0 ? (
            <Consequence tone="warn">
              {money(impact.paidPaise)} received in {plural(impact.payments, 'payment')} is removed
              from your payments and income too.
            </Consequence>
          ) : (
            <Consequence>No payments were recorded for it, so no money changes.</Consequence>
          )}
          {impact.siblings === 0 ? (
            <Consequence>
              It is the only report in this visit, so the visit and its counter slip go too.
            </Consequence>
          ) : (
            <Consequence>
              The other {impact.siblings === 1 ? 'report' : `${impact.siblings} reports`} from this
              visit stay as they are.
            </Consequence>
          )}
          <Consequence>The patient’s details stay in your patient list.</Consequence>
        </>
      }
      note={
        impact.signed ? (
          <>
            Wrongly issued? <b>Revoke</b> it instead: the patient’s link then says the report was
            withdrawn, and the record is kept. If you pay LabView per report, a signed report is
            still counted on your bill. This cannot be undone from the app; a note of who deleted it
            is kept in the history.
          </>
        ) : (
          <>This cannot be undone from the app. A note of who deleted it is kept in the history.</>
        )
      }
      confirmLabel="Delete report"
      typeToConfirm={impact.signed}
      blockedReason={impact.blockedReason}
      onClose={onClose}
      onConfirm={async () => {
        onBeforeDelete?.();
        await deleteReport(ws.db, ctx, reportId);
        void ws.engine.refreshCounts();
        void ws.engine.syncNow();
        onDeleted();
      }}
    />
  );
}
